import React from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toccoupon } from "../../assets/img";

interface BenefitItem {
    icon?: string; // Icon class name (e.g., "fa fa-lightbulb" or image path)
    text: string;
}

interface TOCDynamicPageProps {
    title?: string;
    location?: string;
    paragraphs?: string[];
    companyName?: string;
    images?: string[];
    highlightWord?: string; // Word to underline in title (default: "SERVICES")
    benefits?: BenefitItem[]; // Why Choose Us benefits
    whyChooseUsTitle?: string;
    footerTitle?: string; // "GET STARTED TODAY!" text
    phoneNumbers?: string[]; // Array of phone numbers
    socialMedia?: {
        facebook?: string;
        twitter?: string;
        instagram?: string;
    };
    logoText?: {
        main?: string;
        sub?: string;
    };
    coupon?: {
        discount?: string; // e.g., "10% OFF"
        title?: string; // e.g., "SPECIAL OFFER"
        description?: string; // e.g., "On All Rugs"
    };
}

function TOCDynamicPage() {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    
    // Get data from route state (passed via NavLink state prop)
    const routeState = location.state as TOCDynamicPageProps | null;
    
    // Get data from query parameters as fallback
    const getQueryParam = (key: string, defaultValue: string = "") => {
        return searchParams.get(key) || defaultValue;
    };
    
    // Priority: route state > query params > defaults
    const title = routeState?.title || getQueryParam("title", "OUR DRY CLEANING SERVICES");
    const locationParam = routeState?.location || getQueryParam("location", "");
    const companyName = routeState?.companyName || getQueryParam("companyName", "TOUCH OF CLASS");
    const highlightWord = routeState?.highlightWord || getQueryParam("highlightWord", "SERVICES");
    
    // Handle paragraphs - can be passed as JSON string in query params or array in state
    let paragraphs: string[] = routeState?.paragraphs || [];
    if (paragraphs.length === 0) {
        const paragraphsParam = getQueryParam("paragraphs", "");
        if (paragraphsParam) {
            try {
                paragraphs = JSON.parse(paragraphsParam);
            } catch (e) {
                paragraphs = [];
            }
        } else {
            paragraphs = [];
        }
    }
    
    // Handle images - can be passed as JSON string in query params or array in state
    let images: string[] = routeState?.images || [];
    if (images.length === 0) {
        const imagesParam = getQueryParam("images", "");
        if (imagesParam) {
            try {
                images = JSON.parse(imagesParam);
            } catch (e) {
                images = [];
            }
        }
    }
    
    // Handle benefits - Why Choose Us section
    let benefits: BenefitItem[] = routeState?.benefits || [];
    if (benefits.length === 0) {
        const benefitsParam = getQueryParam("benefits", "");
        if (benefitsParam) {
            try {
                benefits = JSON.parse(benefitsParam);
            } catch (e) {
                benefits = [
                    { icon: "fa fa-lightbulb", text: "Personalized Experience" },
                    { icon: "fa fa-tag", text: "Affordable Pricing" },
                    { icon: "fa fa-chair", text: "Convenience" },
                    { icon: "fa fa-award", text: "Quality" },
                    { icon: "fa fa-truck", text: "Express Delivery" },
                    { icon: "fa fa-check-circle", text: "Instant Order Update" }
                ];
            }
        } else {
            benefits = [
                { icon: "fa fa-lightbulb", text: "Personalized Experience" },
                { icon: "fa fa-tag", text: "Affordable Pricing" },
                { icon: "fa fa-chair", text: "Convenience" },
                { icon: "fa fa-award", text: "Quality" },
                { icon: "fa fa-truck", text: "Express Delivery" },
                { icon: "fa fa-check-circle", text: "Instant Order Update" }
            ];
        }
    }
    
    const whyChooseUsTitle = routeState?.whyChooseUsTitle || getQueryParam("whyChooseUsTitle", "WHY CHOOSE US");
    const footerTitle = routeState?.footerTitle || getQueryParam("footerTitle", "GET STARTED TODAY!");
    
    // Handle phone numbers
    let phoneNumbers: string[] = routeState?.phoneNumbers || [];
    if (phoneNumbers.length === 0) {
        const phoneParam = getQueryParam("phoneNumbers", "");
        if (phoneParam) {
            try {
                phoneNumbers = JSON.parse(phoneParam);
            } catch (e) {
                phoneNumbers = ["0208-989-4484", "0781-114-8424"];
            }
        } else {
            phoneNumbers = ["0208-989-4484", "0781-114-8424"];
        }
    }
    
    // Handle social media
    const socialMedia = routeState?.socialMedia || {
        facebook: getQueryParam("facebook", "#"),
        twitter: getQueryParam("twitter", "#"),
        instagram: getQueryParam("instagram", "#")
    };
    
    // Handle logo text
    const logoText = routeState?.logoText || {
        main: getQueryParam("logoMain", "Touch of Class"),
        sub: getQueryParam("logoSub", "DRY CLEANERS")
    };
    
    // Handle coupon
    let coupon = routeState?.coupon;
    if (!coupon) {
        const couponParam = getQueryParam("coupon", "");
        if (couponParam) {
            try {
                coupon = JSON.parse(couponParam);
            } catch (e) {
                coupon = undefined;
            }
        }
    }
    
    // Function to render paragraph with bold text
    const renderParagraph = (text: string) => {
        const parts = text.split(`**${companyName}**`);
        if (parts.length > 1) {
            return (
                <>
                    {parts[0]}
                    <strong>{companyName}</strong>
                    {parts[1]}
                </>
            );
        }
        return text;
    };

    // Function to render title with centered underline
    const renderTitle = () => {
        const fullTitle = `${title} ${locationParam ? `IN ${locationParam}` : ""}`;
        return (
            <h2 className="toc-section-title">
                {fullTitle}
                <span className="toc-underline"></span>
            </h2>
        );
    };

    return (
        <>
            <div className="toc-dry-cleaning-section">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="toc-content-wrapper">
                                {/* Title Section */}
                                <div className="toc-title-section">
                                    {renderTitle()}
                                </div>

                                {/* Coupon Section */}
                                {coupon && (
                                    <div className="toc-coupon-section">
                                        <img 
                                            src={toccoupon} 
                                            alt="Special Offer Coupon" 
                                            className="toc-coupon-image"
                                        />
                                    </div>
                                )}

                                {/* Text Content Section */}
                                <div className="toc-text-section">
                                    {paragraphs.map((paragraph, index) => (
                                        <p key={index} className="toc-paragraph text-center">
                                            {renderParagraph(paragraph)}
                                        </p>
                                    ))}
                                </div>

                                {/* Images Section */}
                                {images && images.length > 0 && (
                                    <div className="toc-images-section">
                                        <div className="row">
                                            {images.map((image, index) => (
                                                <div key={index} className="col-md-4 col-sm-6">
                                                    <div className="toc-image-wrapper">
                                                        <img 
                                                            src={image} 
                                                            alt={`Dry cleaning service ${index + 1}`}
                                                            className="img-fluid toc-image"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Why Choose Us Section */}
                                <div className="toc-why-choose-section pb-0">
                                    <div className="toc-why-choose-title-section">
                                        <h2 className="toc-why-choose-title">{whyChooseUsTitle}</h2>
                                        <span className="toc-why-choose-underline"></span>
                                    </div>
                                    <div className="toc-benefits-grid">
                                        <div className="row">
                                            {benefits.map((benefit, index) => (
                                                <div key={index} className="col-md-4 col-sm-6">
                                                    <div className="toc-benefit-item">
                                                        {benefit.icon && (
                                                            <div className="toc-benefit-icon">
                                                                {benefit.icon.startsWith('fa ') || benefit.icon.startsWith('fas ') || benefit.icon.startsWith('far ') || benefit.icon.startsWith('fab ') ? (
                                                                    <i className={benefit.icon}></i>
                                                                ) : (
                                                                    <img src={benefit.icon} alt={benefit.text} />
                                                                )}
                                                            </div>
                                                        )}
                                                        <span className="toc-benefit-text">{benefit.text}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default TOCDynamicPage;